<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-md-8 mx-auto">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="d-flex align-items-center">
                            <p class="mb-0">Editar Unidad: {{ unit.name || 'Cargando...' }}</p>
                        </div>
                    </div>
                    <div class="card-body">
                        <div v-if="loading" class="text-center py-5">
                            Cargando datos de la unidad...
                        </div>
                        <form v-else @submit.prevent="submitUnit">
                            <div class="row">
                                <div class="col-md-6">
                                    <label class="form-control-label">Código (Abreviatura)</label>
                                    <argon-input
                                        v-model="unit.code"
                                        type="text"
                                        placeholder="Ej: UN, LT, CAJ"
                                        :disabled="true"
                                    />
                                    <small class="text-muted">El código no es editable.</small>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-control-label">Nombre</label>
                                    <argon-input v-model="unit.name" type="text" placeholder="Ej: Unidad, Litro, Caja" />
                                    <small v-if="submitted && v$.unit.name.$error" class="text-danger">Nombre de la unidad requerido.</small>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-control-label">Estado</label>
                                    <select class="form-control" v-model="unit.state">
                                        <option value="1">Activo</option>
                                        <option value="2">Inactivo</option>
                                    </select>
                                    <small v-if="submitted && v$.unit.state.$error" class="text-danger">Estado requerido.</small>
                                </div>
                            </div>
                            <argon-button
                                type="submit"
                                class="mt-4"
                                color="success"
                                fullWidth
                                size="lg"
                            >
                                Actualizar Unidad
                            </argon-button>
                            <argon-button
                                type="button"
                                class="mt-2"
                                color="success"
                                fullWidth
                                size="lg"
                                @click="$router.push('/settings/units')"
                            >
                                Cancelar
                            </argon-button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import axios from "axios";
import Swal from 'sweetalert2';

export default {
    name: "EditUnit",
    components: { ArgonInput, ArgonButton },
    setup: () => ({ v$: useVuelidate() }),
    data() {
        // Obtenemos el ID de la ruta
        const unitId = parseInt(this.$route.params.id);
        const companyId = parseInt(localStorage.getItem('companyId'));

        return {
            loading: true,
            submitted: false,
            unitId: unitId,
            unit: {
                id: unitId,
                code: '',
                name: '',
                state: 1,
                company: companyId || null // Aseguramos el company ID para el payload
            }
        };
    },
    validations() {
        return {
            unit: {
                name: { required: helpers.withMessage('El nombre es requerido', required) },
                state: { required: helpers.withMessage('El estado es requerido', required) },
                company: { required: helpers.withMessage('ID de compañía faltante', required) }
            }
        };
    },
    mounted() {
        if (this.unitId) {
            this.fetchUnitData();
        } else {
            Swal.fire('Error de Ruta', 'ID de unidad no proporcionado.', 'error');
            this.$router.push('/settings/units');
        }
    },
    methods: {
        async fetchUnitData() {
            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;

            const config = { headers: { 'Authorization': `Bearer ${token}` } };

            try {
                // GET a la API para obtener los datos de la unidad específica por ID
                const response = await axios.get(`${apiURL}units/${this.unitId}`, config);

                // Mapear los datos recibidos al objeto unit
                this.unit.name = response.data.name;
                this.unit.code = response.data.code;
                this.unit.state = response.data.state;
                // Opcional: validar que el company_id del registro coincida con el del usuario
                // if (response.data.company !== this.unit.company) {
                //     throw new Error("Acceso denegado: La unidad no pertenece a su compañía.");
                // }

            } catch (error) {
                console.error("Error al obtener datos de la unidad:", error.response || error);
                Swal.fire('Error', 'No se pudieron cargar los datos de la unidad. Es posible que no exista o no pertenezca a su compañía.', 'error');
                this.$router.push('/settings/units');
            } finally {
                this.loading = false;
            }
        },
        async submitUnit() {
            this.submitted = true;
            this.v$.$touch();

            if (this.v$.$invalid) {
                Swal.fire({
                    title: 'Datos inválidos',
                    text: 'Por favor, revisa el nombre y el estado.',
                    icon: 'warning',
                    confirmButtonColor: '#0b9257'
                });
                return;
            }

            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            // 🛑 CRÍTICO: Usamos el método PUT al endpoint de unidad (ej: units/1)
            try {
                // Solo enviamos los campos que se pueden actualizar.
                const updatePayload = {
                    name: this.unit.name,
                    state: this.unit.state,
                    company: this.unit.company // Es buena práctica enviarlo, aunque el WHERE se hace por ID.
                };

                await axios.put(`${apiURL}units/${this.unitId}`, updatePayload, config);

                Swal.fire({
                    title: 'Actualizado',
                    text: 'La unidad de medida fue actualizada correctamente.',
                    icon: 'success',
                    confirmButtonColor: '#0b9257'
                }).then(() => {
                    this.$router.push('/settings/units');
                });
            } catch (err) {
                console.error("Error al actualizar unidad:", err.response || err);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo actualizar la unidad. Verifique los datos.',
                    icon: 'error',
                    confirmButtonColor: '#0b9257'
                });
            }
        }
    }
};
</script>